import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductQuestionGroupQuestion } from "src/client/generated";
import { SignaturePad } from "../SignaturePad";

interface ProductQuestionInputProps {
  question: ProductQuestionGroupQuestion;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function ProductQuestionInput({ question, value, onChange, disabled = false }: ProductQuestionInputProps) {
  const { t } = useTranslation();

  let input = null;

  switch (question.question_type) {
    case "yes_no":
      input = (
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              className="size-4"
              disabled={disabled}
              checked={value === "yes"}
              onChange={() => onChange("yes")}
            />
            <span>{t("Ja")}</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              className="size-4"
              disabled={disabled}
              checked={value === "no"}
              onChange={() => onChange("no")}
            />
            <span>{t("Nein")}</span>
          </label>
        </div>
      );
      break;

    case "yes_no_with_explanation": {
      // Parse combined value: "yes|text" or "no"
      const isYes = value.startsWith("yes");
      const freeText = isYes && value.includes("|") ? value.split("|").slice(1).join("|") : "";

      input = (
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              className="size-4"
              disabled={disabled}
              checked={isYes}
              onChange={() => onChange(freeText ? `yes|${freeText}` : "yes|")}
            />
            <span>{t("Ja")}</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              className="size-4"
              disabled={disabled}
              checked={!isYes && value === "no"}
              onChange={() => onChange("no")}
            />
            <span>{t("Nein")}</span>
          </label>
          {isYes ? (
            <textarea
              className="mt-2 w-full rounded-lg border p-4"
              value={freeText}
              onChange={(e) => onChange(`yes|${e.target.value}`)}
              disabled={disabled}
              maxLength={question.max_text_length}
              rows={3}
              placeholder={t("Bitte geben Sie Details an...")}
            />
          ) : null}
        </div>
      );
      break;
    }

    case "number":
      input = (
        <input
          className="h-12 w-full rounded-full border px-4"
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={question.is_required}
          min={question.number_min}
          max={question.number_max}
          placeholder={
            question.number_min !== undefined && question.number_max !== undefined
              ? `${question.number_min} - ${question.number_max}`
              : undefined
          }
        />
      );
      break;

    case "freetext":
      input = (
        <textarea
          className="w-full rounded-lg border p-4"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={question.is_required}
          maxLength={question.max_text_length}
          rows={4}
          placeholder={t("Ihre Antwort...")}
        />
      );
      break;

    case "checkbox":
      input = (
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={value === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            disabled={disabled}
            required={question.is_required}
            className="size-5 shrink-0 accent-blue-600"
          />
          <span className="text-sm">{t("Zustimmen")}</span>
        </label>
      );
      break;

    case "stars":
      input = (
        <StarsField
          min={question.stars_min}
          max={question.stars_max}
          value={value ? Number.parseInt(value, 10) : undefined}
          onChange={(val) => onChange(val !== undefined ? String(val) : "")}
          disabled={disabled}
        />
      );
      break;

    case "signature":
      input = (
        <>
          <SignaturePad value={value} onChange={onChange} disabled={disabled} />
          {/* Hidden input for form validation */}
          <input type="hidden" value={value} required={question.is_required} />
        </>
      );
      break;

    case "select_with_custom":
      // Parse select_options - could be JSON array or comma-separated string
      let options: string[] = [];
      if (question.select_options) {
        try {
          // Try parsing as JSON first
          const parsed = JSON.parse(question.select_options);
          if (Array.isArray(parsed)) {
            options = parsed;
          } else {
            options = question.select_options
              .split(",")
              .filter(Boolean)
              .map((opt) => opt.trim());
          }
        } catch {
          // If not JSON, treat as comma-separated string
          options = question.select_options
            .split(",")
            .filter(Boolean)
            .map((opt) => opt.trim());
        }
      }
      input = (
        <SelectWithCustomField
          options={options}
          maxLength={question.max_text_length}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );
      break;

    default:
      input = (
        <input
          className="h-12 w-full rounded-full border px-4"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={question.is_required}
        />
      );
  }

  return (
    <fieldset className="min-w-0 space-y-2">
      <legend className="text-sm font-medium">
        <span>{question.question_text}</span>
        {question.is_required ? <span className="text-red-500"> *</span> : null}
      </legend>
      {input}
    </fieldset>
  );
}

// Stars rating component
function StarsField({
  min,
  max,
  value,
  onChange,
  disabled = false,
}: {
  min?: number;
  max?: number;
  value?: number;
  onChange?: (value: number | undefined) => void;
  disabled?: boolean;
}) {
  const lowerBound = typeof min === "number" ? min : 1;
  const upperBound = typeof max === "number" ? max : 5;
  const [selected, setSelected] = useState<number | undefined>(value);
  const [hovered, setHovered] = useState<number | undefined>(undefined);
  const total = upperBound - lowerBound + 1;

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: total }).map((_, idx) => {
        const starValue = lowerBound + idx;
        const fillCutoff = typeof hovered === "number" ? hovered : (selected ?? 0);
        const isFilled = starValue <= fillCutoff;
        return (
          <button
            key={starValue}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                setSelected(starValue);
                onChange?.(starValue);
              }
            }}
            onMouseEnter={() => !disabled && setHovered(starValue)}
            onMouseLeave={() => !disabled && setHovered(undefined)}
            className="select-none disabled:opacity-50"
            aria-label={`${starValue}`}
          >
            <div
              data-filled={isFilled}
              className="text-2xl data-[filled=false]:text-gray-300 data-[filled=true]:text-yellow-500"
            >
              {isFilled ? "★" : "☆"}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Select with custom text input
function SelectWithCustomField({
  options,
  maxLength,
  value,
  onChange,
  disabled = false,
}: {
  options: string[];
  maxLength?: number;
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [showCustomInput, setShowCustomInput] = useState(false);
  const isCustomValue = Boolean(value && !options.includes(value));

  useEffect(() => {
    setShowCustomInput(isCustomValue);
  }, [isCustomValue, value, options]);

  return (
    <div className="space-y-2">
      <select
        className="h-12 w-full rounded-full border px-4"
        value={showCustomInput ? "__custom__" : value}
        onChange={(e) => {
          if (e.target.value === "__custom__") {
            setShowCustomInput(true);
            onChange?.("");
          } else {
            setShowCustomInput(false);
            onChange?.(e.target.value);
          }
        }}
        disabled={disabled}
      >
        <option value="">{t("Bitte wählen...")}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        <option value="__custom__">{t("Sonstiges (eigener Text)")}</option>
      </select>
      {showCustomInput ? (
        <input
          type="text"
          maxLength={maxLength}
          placeholder={t("Geben Sie Ihre Antwort ein...")}
          className="h-12 w-full rounded-full border px-4"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}
